import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import GEOLOCATION_MATCHINGS from '~/../config/geolocation.json'

import { ShopifyCountryCode as CountryCode } from '@unlikelystudio/commerce-connector'

import useGeolocationCookie from '~/hooks/useGeolocationCookie'
import useGeolocationMutation from '~/hooks/useGeolocationMutation'
import useLocale from '~/hooks/useLocale'

type GeolocationContextProps = {
  countryISO?: CountryCode
  preferedLocation?: string
  isShippingCountry?: boolean
  displayGlobalPopIn?: boolean
  displayCartPopIn?: boolean
  setDisplayGlobalPopIn?: (boolean) => void
  setDisplayCartPopIn?: (boolean) => void
}

export const Context = createContext<GeolocationContextProps>({})

export function useGeolocation() {
  return useContext(Context)
}

export type GeolocationProviderProps = {
  children?: JSX.Element | JSX.Element[]
}

const NON_SHIPPING_AREA = 'NON_SHIPPING_AREA'

export default function GeolocationProvider({
  children,
}: GeolocationProviderProps) {
  const { data } = useGeolocationMutation({
    refetchOnWindowFocus: false,
  })

  const { getGlobalGeoLocation } = useGeolocationCookie()

  const [countryISO, setCountryISO] = useState(null)
  const locale = useLocale()

  useEffect(() => {
    if (data) {
      setCountryISO(data?.country ?? CountryCode.Fr)
    }
  }, [data])

  // Get the good locale from your country iso code
  const preferedLocation = countryISO
    ? GEOLOCATION_MATCHINGS?.[countryISO] ?? NON_SHIPPING_AREA
    : undefined

  // Is a shipping area area depending on geolocation file
  const isShippingCountry = preferedLocation !== NON_SHIPPING_AREA

  // Has already a cookie
  const isGlobalGeolocCookie = getGlobalGeoLocation()

  // Wrong locale from detection
  const isWrongLocale = locale !== preferedLocation

  // Locale state of the popin
  const [displayGlobalPopIn, setDisplayGlobalPopIn] = useState(null)
  const [displayCartPopIn, setDisplayCartPopIn] = useState(null)

  // Need to display the popin
  const needPopIn =
    (!isShippingCountry || isWrongLocale) && !isGlobalGeolocCookie

  useEffect(() => {
    if (preferedLocation) {
      setDisplayGlobalPopIn(needPopIn)
    }
  }, [preferedLocation, needPopIn])

  const providerValue = useMemo(
    () => ({
      countryISO,
      preferedLocation,
      displayGlobalPopIn,
      displayCartPopIn,
      setDisplayGlobalPopIn,
      setDisplayCartPopIn,
      isShippingCountry,
    }),
    [countryISO, preferedLocation, displayCartPopIn, displayGlobalPopIn],
  )

  return <Context.Provider value={providerValue}>{children}</Context.Provider>
}
